import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { getUserRole, getUserBadge } from '@/utils/navigation';
import { ChevronDown, ChevronUp, CircleCheck, Ticket } from 'lucide-react';
import type { Event } from '@/types/event';

interface SubscriptionCardProps {
  event?: Event;
  quota?: any;
  showDetails: boolean;
  onToggleDetails: () => void;
  variant?: 'default' | 'liveSessions';
  userRegistrationsCount?: number;
  userSessionQuota?: number;
  hasValidSubscription?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  event, 
  quota: myQuotas = {},
  showDetails, 
  onToggleDetails,
  variant = 'default',
  userRegistrationsCount,
  userSessionQuota,
  hasValidSubscription = false,
}) => {
  const { user } = useUser();
  
  const userRole = getUserRole(user?.fk_role_id || user?.profile?.fk_role_id || user?.role || 'visitor');
  const userBadge = getUserBadge(userRole);
  const isPlus = userBadge === 'PLUS';
  // console.log(myQuotas);
  const pnQuota = myQuotas?.summary?.professional_network_building?.total_quota || 0;
  const pnRemain = myQuotas?.summary?.professional_network_building?.total_remaining || 0;
  const pnTotal = Math.max(pnQuota, pnRemain);
  const pnUsed = pnTotal - pnRemain;
  
  const usedSlots = variant === 'liveSessions' && typeof userRegistrationsCount === 'number'
    ? userRegistrationsCount
    : pnUsed ?? 0;
  const baseTotalSlots = variant === 'liveSessions' ? (event?.max_attendees ?? event?.next_schedule?.max_seat ?? 0) : pnTotal;
  
  // Check if user has a PLUS subscription (hide upgrade button only for PLUS users)
  // For both variants, check if user has PLUS badge - free plan users should see upgrade button
  const hasPlusSubscription = isPlus;
  
  const liveSessionsCap = typeof userSessionQuota === 'number' ? userSessionQuota : 6;
  const totalSlots = variant === 'liveSessions'
    ? liveSessionsCap
    : baseTotalSlots;
  const effectiveUsed = variant === 'liveSessions'
    ? Math.min(usedSlots, liveSessionsCap)
    : usedSlots;
  const remainingSlots = totalSlots > 0 ? Math.max(totalSlots - effectiveUsed, 0) : 0;
  const usagePercent = totalSlots > 0 
    ? Math.min(100, (effectiveUsed / totalSlots) * 100) 
    : (effectiveUsed > 0 ? 100 : 0);
  const barPercent = usagePercent;
  const totalLabel = totalSlots > 0 ? totalSlots : '∞';
  
  // Only show session count if user has subscription
  // For liveSessions, use hasValidSubscription to determine if they can see session info
  // For default variant, always show events left
  const headerCount = variant === 'liveSessions'
    ? (hasValidSubscription ? remainingSlots : null)
    : pnRemain || 0;
  const headerLabel = variant === 'liveSessions'
    ? (hasValidSubscription ? `session${headerCount <= 1 ? '' : 's'} left` : null)
    : `event${headerCount < 2 ? '' : 's'} left`;

  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [expandedTop, setExpandedTop] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Only apply scroll following on mobile
    if (!isMobile) {
      setExpandedTop(null);
      return;
    }

    let rafId: number | null = null;
    
    const updatePosition = () => {
      if (showDetails && buttonRef.current) {
        // Use viewport coordinates for fixed positioning, update on scroll
        const rect = buttonRef.current.getBoundingClientRect();
        setExpandedTop(rect.bottom + 8); // 8px for mt-2
      } else {
        setExpandedTop(null);
      }
    };

    const smoothUpdatePosition = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(updatePosition);
    };

    updatePosition();

    if (showDetails) {
      // Use requestAnimationFrame for smooth updates on scroll (mobile only)
      window.addEventListener('scroll', smoothUpdatePosition, { passive: true });
      window.addEventListener('resize', smoothUpdatePosition, { passive: true });
      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener('scroll', smoothUpdatePosition);
        window.removeEventListener('resize', smoothUpdatePosition);
      };
    }
  }, [showDetails, isMobile]);

  const renderUsageSection = () => {
    if (variant === 'liveSessions') {
      // Only show usage section if user has a valid subscription
      if (!hasValidSubscription) {
        return null;
      }
      return (
        <div className="flex flex-col bg-gradient-to-br from-slate-900/60 via-blue-950/40 to-slate-900/60 border border-white/10 rounded-xl p-4 mb-6 space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-300 uppercase tracking-wide">
            <span>Session Allowance</span>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-200">
            <span>Registered</span>
            <span className="font-semibold">{effectiveUsed} / {totalLabel}</span>
          </div>
          <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-sky-400 via-indigo-500 to-fuchsia-500 transition-all duration-500" 
              style={{ width: `${barPercent}%` }}
            ></div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col bg-orange-900/30 text-white text-left transition-colors px-5 py-3 rounded-xl border-2 border-orange-500/20 space-y-4 mb-6">
        <div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300 text-sm mb-1">Event Registrations</span>
            <Ticket size={18} className="text-orange-400 -mt-2" />
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-white text-sm font-thin">Used: {usedSlots}</span>
            <span className="text-white text-sm font-thin">
              Total: {totalSlots > 0 ? totalSlots : 'Unlimited'}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
            <div 
              className="bg-orange-500 h-2 rounded-full transition-all duration-500" 
              style={{ width: `${barPercent}%` }}
            ></div>
          </div>
          <div className="text-orange-300 text-sm font-medium">
            {totalSlots > 0 ? `${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} remaining` : 'No limit'}
          </div>
        </div>
      </div>
    );
  };

  return (
  <div className="ml-auto items-end justify-end text-right relative mb-0 overflow-visible" style={{ width: '100%', maxWidth: '350px', zIndex: 20, minWidth: variant === 'liveSessions' ? '0' : '300px' }}>
    <button 
      ref={isMobile ? buttonRef : undefined}
      onClick={onToggleDetails}
      type="button"
      className="flex flex-row items-start bg-gradient-to-br hover:bg-gradient-to-bl from-blue-900 via-blue-950 to-black text-white transition-colors px-5 py-2 mb-3 rounded-lg border-2 border-blue-500/20 w-full relative z-20 overflow-visible"
      style={{ 
        minHeight: 'auto', 
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        whiteSpace: 'normal',
        wordWrap: 'break-word'
      }}
    >
      <div className="flex flex-col space-y-1 flex-1" style={{ flex: '1 1 auto', overflow: 'visible' }}>
        <div className="flex items-center justify-start flex-nowrap gap-1 font-bold whitespace-nowrap" style={{ overflow: 'visible' }}>
          <span className="text-base flex-shrink-0 uppercase">SKILLSTURE</span>                                        
          {isPlus && <sup className="text-orange-600 text-sm">+</sup>}
        </div>
        {headerLabel && headerCount !== null && (
          <div className="flex items-start justify-start text-left" style={{ overflow: 'visible' }}>                    
            <span className="text-sm text-gray-300" style={{ 
              wordBreak: 'break-word', 
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              overflow: 'visible'
            }}>
              <span className="font-bold text-white">{headerCount}</span> {headerLabel}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-start justify-center flex-shrink-0 ml-2" style={{ flexShrink: 0, overflow: 'visible' }}>
        {showDetails ? <ChevronUp size={22} className="flex-shrink-0" /> : <ChevronDown size={22} className="flex-shrink-0" />}
      </div>
    </button>
    
    {showDetails && (
      <div 
        className={isMobile 
          ? "fixed left-0 right-0 opacity-100" 
          : ((variant === 'liveSessions' ? 'relative mt-2' : 'absolute mt-4') + " w-full z-30 opacity-100 transition-all duration-300 ease-in-out md:absolute md:top-full md:left-auto md:right-0 md:w-auto md:min-w-full")
        }
        style={isMobile ? {
          // Mobile only: use calculated top position, full width breaking out
          top: expandedTop !== null ? `${expandedTop}px` : undefined,
          left: '0',
          right: '0',
          width: '100vw',
          zIndex: 1000,
          transition: 'none', // No transition on mobile to prevent bouncing
          display: expandedTop === null ? 'none' : 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        } : {
          // Desktop: exact original styles - no inline z-index, let CSS class handle it
          minWidth: '100%'
        }}
      >
        <div className="flex flex-col bg-gradient-to-br from-blue-900 via-blue-950 to-black text-white text-left px-5 py-4 rounded-xl border-2 border-blue-500/20 shadow-2xl w-full md:w-auto md:min-w-full overflow-visible" style={isMobile ? { maxWidth: 'calc(100vw - 48px)', width: 'calc(100vw - 48px)', marginLeft: 'auto', marginRight: 'auto' } : { maxWidth: '100%' }}>
          <h3 className="text-lg font-normal text-white mb-6">Subscription Details</h3>
          
          <div className="flex flex-row items-center justify-between mb-6 bg-white/10 backdrop-blur-lg rounded-xl p-3 flex-wrap gap-2">
            <div className="flex flex-col items-start justify-between">
              <span className="text-gray-300 text-sm mb-1">Current Plan</span>
            </div>
            <div className="w-full flex flex-row items-center justify-between">
              <span className="text-white font-semibold uppercase">SKILLSTURE {userBadge}</span>          
              <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full border border-green-500/30">
                <CircleCheck size={14} className="inline-block mr-2 -mt-0.5" />
                Active
              </span>
            </div>
          </div>

          {renderUsageSection()}

          {!hasPlusSubscription && (
            <button onClick={() => navigate('/profile?tab=subscription')} className="w-full bg-orange-600 text-white py-2 rounded-md font-semibold hover:bg-orange-700 transition-all transform hover:scale-105">
              Upgrade SKILLSTURE
            </button>
          )}
        </div>
      </div>
    )}
  </div>
)};

export default SubscriptionCard;